import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '../components/NavBar';
interface BlogPost {
  title: string;
  description: string;
  slug: string;
  emoji: string;
  publishDate: string;
  readTime: string;
  category: string;
  datePublished: string;
}
const blogPosts: BlogPost[] = [{
  title: '7 Signs of Gaslighting in Texts | AI Detection',
  description: 'Discover 7 signs of gaslighting our AI catches instantly. Upload your texts to detect manipulation, denial, and emotional abuse patterns in seconds.',
  slug: '7-signs-of-gaslighting',
  emoji: '💀',
  publishDate: 'January 14, 2025',
  datePublished: '2025-01-14',
  readTime: '5 min read',
  category: 'Gaslighting'
}, {
  title: 'Is Your Ex a Covert Narcissist? AI Analysis',
  description: 'Discover if your ex is a covert narcissist. Our AI analyzes texts to detect manipulation, gaslighting & hidden control.',
  slug: 'is-your-ex-covert-narcissist',
  emoji: '🎭',
  publishDate: 'March 8, 2025',
  datePublished: '2025-03-08',
  readTime: '6 min read',
  category: 'Narcissism'
}, {
  title: 'Passive-Aggressive Texts: Complete Guide',
  description: 'Learn to spot passive-aggressive texts with our AI guide. Detect sarcasm, backhanded compliments & manipulation.',
  slug: 'how-to-spot-passive-aggressive-texts',
  emoji: '😤',
  publishDate: 'May 22, 2025',
  datePublished: '2025-05-22',
  readTime: '7 min read',
  category: 'Communication'
}, {
  title: 'Narcissism & Gaslighting: Signs Detected',
  description: 'Learn about narcissism and gaslighting. Understand manipulation tactics and how AI detects toxic patterns in texts.',
  slug: 'narcissism-and-gaslighting-explained',
  emoji: '💀',
  publishDate: 'July 15, 2025',
  datePublished: '2025-07-15',
  readTime: '5 min read',
  category: 'Education'
}, {
  title: 'Emotional Drain in Texts: AI Breakdown',
  description: 'Discover why some people make you feel drained. AI analyzes emotional patterns in texts to reveal one-sided relationships.',
  slug: 'why-some-people-drain-you',
  emoji: '😮‍💨',
  publishDate: 'September 3, 2025',
  datePublished: '2025-09-03',
  readTime: '8 min read',
  category: 'Emotional Health'
}, {
  title: '7 Texting Patterns That Drain You | Part 2',
  description: 'More subtle patterns that drain your energy. AI detects over-explaining, one-uppers, emoji confusion & more.',
  slug: 'emotional-drain-part-2',
  emoji: '😭',
  publishDate: 'November 16, 2025',
  datePublished: '2025-11-16',
  readTime: '7 min read',
  category: 'Emotional Health'
}];
// Group posts by category
const categories = ['Gaslighting', 'Narcissism', 'Communication', 'Education', 'Emotional Health'];
export function BlogIndexPage() {
  // JSON-LD Schema for Blog Listing
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    headline: 'ToxicDetector Blog',
    description: 'Expert insights on toxic relationships, gaslighting, narcissism, and emotional manipulation. Learn how to spot red flags with AI relationship analysis.',
    url: 'https://toxicdetector.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      url: 'https://toxicdetector.com/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/images/logo.svg'
      }
    },
    blogPost: blogPosts.map(post => ({
      '@type': 'BlogPosting',
      headline: post.title,
      image: `https://toxicdetector.com/images/blog/${post.slug}.svg`,
      url: `https://toxicdetector.com/blog/${post.slug}`,
      datePublished: post.datePublished,
      description: post.description
    }))
  };
  return <>
      <Helmet data-id="element-3612" data-mp="yfhthytgkxec">
        {/* Primary Meta Tags */}
        <title data-id="element-3613" data-mp="2rwx64jbnrqlc">
          ToxicDetector Blog: Relationship Insights, Gaslighting Guides & Toxic
          Pattern Analysis
        </title>
        <meta name="description" content="Expert insights on toxic relationships, gaslighting, narcissism, and emotional manipulation. Learn how to spot red flags using AI-powered relationship analysis." data-id="element-3614" data-mp="2wufikjovwny3" />
        <meta name="keywords" content="toxic relationship insights, gaslighting signs, narcissist red flags, AI relationship analysis blog, toxic patterns education, emotional manipulation guides" data-id="element-3615" data-mp="36ne9w9brmsle" />
        <link rel="canonical" href="https://toxicdetector.com/blog" data-id="element-3616" data-mp="2ivfvvhkxmj4k" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="ToxicDetector Blog: Relationship Insights & Toxic Pattern Guides" data-id="element-3617" data-mp="2nvlhag0lz9e7" />
        <meta property="og:description" content="Expert guides on gaslighting, manipulation & narcissism. Learn how AI detects toxic patterns through real text conversations." data-id="element-3618" data-mp="3uamcgjht7njq" />
        <meta property="og:url" content="https://toxicdetector.com/blog" data-id="element-3619" data-mp="1nm2j9i35gmp1" />
        <meta property="og:type" content="website" data-id="element-3620" data-mp="1iotzxe69mcm7" />
        <meta property="og:image" content="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" data-id="element-3621" data-mp="1dx2hxs16df7o" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-3622" data-mp="13ymr7q6287yp" />
        <meta name="twitter:title" content="ToxicDetector Blog: Insights Into Gaslighting & Toxic Patterns" data-id="element-3623" data-mp="13ws3k43em1qx" />
        <meta name="twitter:description" content="Learn how to spot manipulation, emotional abuse & toxic relationship patterns with AI-powered insights." data-id="element-3624" data-mp="uxyeqzwbefnx" />
        <meta name="twitter:image" content="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" data-id="element-3625" data-mp="2pc7kmw1b3dra" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json" data-id="element-3626" data-mp="infds0ip93kc">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <div className="flex flex-col w-full min-h-screen bg-stone-50" data-id="element-3627" data-mp="nefc9a7gvtoy">
        <NavBar data-id="element-3628" data-mp="ngryhzk9cq7t" />
        <header className="bg-white border-b border-gray-200" data-id="element-3629" data-mp="8psyve1ehc0d">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center" data-id="element-3630" data-mp="8rm5itc0jw8d">
            <div className="flex items-center justify-between" data-id="element-3631" data-mp="dpxez2yec4q2">
              <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900 hover:text-gray-700 transition-colors" data-id="element-3632" data-mp="11egk59m5g1dx">
                🚩 ToxicDetector
              </Link>
              <Link to="/signup" className="bg-purple-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm" data-id="element-3633" data-mp="rh259efvjknq">
                Try Free Scan
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content - Narrower, more focused */}
        <main className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-3634" data-mp="3h387ukjv7isz">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-3635" data-mp="1za2f2fxqw6qz">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-3636" data-mp="287ctga5v0c04" />
            Back to Home
          </Link>

          {/* Hero Section - More editorial, less corporate */}
          <div className="mb-16" data-id="element-3637" data-mp="1bidiuohsyy4r">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight text-gray-900" data-id="element-3638" data-mp="1bj6il5icgnde">
              Relationship Insights & Guides
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3639" data-mp="1kj3819ck5q9i">
              Expert insights on toxic relationships, gaslighting, narcissism,
              and emotional manipulation. Learn to spot red flags and protect
              your mental health.
            </p>
          </div>

          {/* Blog Posts by Category */}
          {categories.map(category => {
          const categoryPosts = blogPosts.filter(post => post.category === category);
          if (categoryPosts.length === 0) return null;
          return <section key={category} className="mb-16" data-id="element-3640" data-mp="1qph7edtnk74e">
                <h2 className="text-2xl font-bold mb-6 text-gray-900" data-id="element-3641" data-mp="1vmposc6z874l">
                  {category}
                </h2>
                <div className="space-y-6" data-id="element-3642" data-mp="20l11qvskixr1">
                  {categoryPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group block bg-white rounded-xl border border-gray-200 p-6 hover:border-purple-200 hover:shadow-md transition-all duration-200" data-id="element-3643" data-mp="25jc82wjnmq4i">
                      <div className="flex items-start gap-4" data-id="element-3644" data-mp="2jdczf58a1auu">
                        {/* Emoji */}
                        <span className="text-3xl flex-shrink-0" data-id="element-3645" data-mp="2obwlr6qs8onq">
                          {post.emoji}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-3646" data-mp="2ycpaf7mahvfz">
                          {/* Category Badge */}
                          <span className="inline-block text-xs font-medium text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full mb-3" data-id="element-3647" data-mp="2ogvztts4sc0u">
                            {post.category}
                          </span>
                          {/* Title */}
                          <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-purple-600 transition-colors leading-snug" data-id="element-3648" data-mp="3h48dfegbd9i0">
                            {post.title}
                          </h3>
                          {/* Description */}
                          <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2" data-id="element-3649" data-mp="33eejihpybz1f">
                            {post.description}
                          </p>
                          {/* Meta Info */}
                          <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-3650" data-mp="3v52j3pbz8wcz">
                            <div className="flex items-center gap-1.5" data-id="element-3651" data-mp="3027lzdvazge9">
                              <Calendar size={14} data-id="element-3652" data-mp="2v3mi94gueel2" />
                              <span data-id="element-3653" data-mp="2v4xvduw6gyou">{post.publishDate}</span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-3654" data-mp="3947120mrcaek">
                              <Clock size={14} data-id="element-3655" data-mp="343t1z834waso" />
                              <span data-id="element-3656" data-mp="342zug4wcph9h">{post.readTime}</span>
                            </div>
                          </div>
                        </div>
                        {/* Arrow */}
                        <ArrowRight size={20} className="flex-shrink-0 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all mt-1" data-id="element-3657" data-mp="3e1z2pmd7favy" />
                      </div>
                    </Link>)}
                </div>
              </section>;
        })}

          {/* CTA Section - Simplified to match landing page */}
          <section className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-3658" data-mp="k9nj3jq71pqr">
            <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-3659" data-mp="p8gwlc5hdgqx">
              Ready to Analyze Your Conversations?
            </h2>
            <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-xl mx-auto" data-id="element-3660" data-mp="aa6fd7ui5lxf">
              Upload your chat screenshots and get instant AI-powered insights
              into toxic patterns, manipulation, and red flags.
            </p>
            <Link to="/signup" className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors" data-id="element-3661" data-mp="1h1r5aieqjbg">
              Start Free Scan
              <ArrowRight size={18} data-id="element-3662" data-mp="3soebtzii1ew4" />
            </Link>
          </section>
        </main>

        {/* Footer - Simplified to match landing page */}
        <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-3663" data-mp="3eriz9x31h5bb">
          <div className="max-w-3xl mx-auto px-6" data-id="element-3664" data-mp="3iqi138th4lyj">
            <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-3665" data-mp="1p80xtbhryxop">
              <div data-id="element-3666" data-mp="1p9lsd143agmk">
                <h3 className="font-bold mb-3 text-gray-900" data-id="element-3667" data-mp="1pa3toc5lzl1c">Product</h3>
                <ul className="space-y-2 text-sm" data-id="element-3668" data-mp="1kg9ffo8rvy9n">
                  <li data-id="element-3669" data-mp="1fhy7jj21fxu2">
                    <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3670" data-mp="1fj99refu4xj2">
                      Home
                    </Link>
                  </li>
                  <li data-id="element-3671" data-mp="284soi5zncf2s">
                    <Link to="/signup" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3672" data-mp="238bpuhutmibg">
                      Sign Up
                    </Link>
                  </li>
                  <li data-id="element-3673" data-mp="1u6ua50lbsina">
                    <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3674" data-mp="1u9glzcf91kbb">
                      Login
                    </Link>
                  </li>
                </ul>
              </div>
              <div data-id="element-3675" data-mp="2uhevb1d2riaj">
                <h3 className="font-bold mb-3 text-gray-900" data-id="element-3676" data-mp="2udr2k4fgezwv">Resources</h3>
                <ul className="space-y-2 text-sm" data-id="element-3677" data-mp="2yhfawpbwtzj6">
                  <li data-id="element-3678" data-mp="33fqisuin9zyr">
                    <a href="/blog" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3679" data-mp="33fgsf5totqsj">
                      Blog
                    </a>
                  </li>
                  <li data-id="element-3680" data-mp="3d8eflp9mk3xh">
                    <a href="/privacy" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3681" data-mp="3i4wiskpb3u7p">
                      Privacy Policy
                    </a>
                  </li>
                  <li data-id="element-3682" data-mp="3r5b77sxkhpff">
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3683" data-mp="3r3gv9449x7nh">
                      Terms of Service
                    </a>
                  </li>
                </ul>
              </div>
              <div data-id="element-3684" data-mp="11jq2vh21qc20">
                <h3 className="font-bold mb-3 text-gray-900" data-id="element-3685" data-mp="wika5j8dyosh">About</h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-3686" data-mp="wiafhicebtgg">
                  AI-powered relationship analysis to help you spot toxic
                  patterns and protect your mental health.
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-8 text-center" data-id="element-3687" data-mp="iqcdrsl0kzub">
              <p className="text-gray-500 text-sm" data-id="element-3688" data-mp="dsax1eb29cm0">
                © {new Date().getFullYear()} ToxicDetector. All rights
                reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>;
}